import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale } from '../../componenti/base.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import type { Mediatore } from '../../servizi/mediazioni.ts'

/**
 * Unione dei mediatori doppioni (465; richiesta di CIDIS del 19/10/2026). Si sceglie il mediatore da
 * tenere e i doppioni; prima di confermare si vede quante mediazioni, interventi e servizi passano al
 * mediatore tenuto. Il gestionale propone i probabili doppioni (stesso nome, nome e cognome invertiti,
 * una o due lettere di differenza), ma l'unione si conferma sempre a mano: due persone diverse
 * possono avere lo stesso nome.
 */

type Usi = { mediazioni: number; interventi: number; servizi: number; erogazioni: number }
export type VoceUnione = {
  id: string; nome: string; cognome: string; attivo: boolean; utente_id: string | null
  dal_vecchio_archivio?: boolean; usi?: number
}
type Gruppo = { motivi: ('stesso_nome' | 'nomi_invertiti' | 'simili')[]; proposto: string; mediatori: VoceUnione[] }
type Anteprima = {
  tenere: { id: string; nome: string; cognome: string }
  doppioni: { id: string; nome: string; cognome: string; dal_vecchio_archivio: boolean; usi: Usi }[]
  righe: Usi & { totale: number }
  mediazioni_in_programma: number
  lingue: string[]
  utenza: 'nessuna' | 'resta' | 'passa'
  attivo_dopo: boolean
}

const MOTIVI = { stesso_nome: 'stesso nome', nomi_invertiti: 'nome e cognome invertiti', simili: 'nomi quasi uguali' }
const quanti = (n: number, uno: string, tanti: string) => (n === 1 ? `1 ${uno}` : `${n} ${tanti}`)
const nomeDi = (m: { nome: string; cognome: string }) => `${m.cognome} ${m.nome}`

/** I probabili doppioni dell'albo, uno per riga, da rivedere e unire. */
export function ProposteDoppioni({ chiudi, rivedi }: { chiudi: () => void; rivedi: (g: Gruppo) => void }) {
  const [gruppi, setGruppi] = useState<Gruppo[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    api.get<{ gruppi: Gruppo[] }>('/mediatori/doppioni').then((r) => setGruppi(r.gruppi))
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])

  return (
    <Modale larga titolo="Probabili doppioni" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>}>
      <p className="piccolo">
        Nomi che probabilmente sono la stessa persona. Nessuno si unisce da solo: apri «Rivedi», scegli quale tenere
        e conferma. Se due mediatori sono la stessa persona ma qui non compaiono, usa «Unisci» nella riga di uno dei due.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!gruppi && !errore && <p className="tenue">Cerco i doppioni…</p>}
      {gruppi?.length === 0 && <p>Nessun probabile doppione nell’albo.</p>}
      {gruppi && gruppi.length > 0 && (
        <ul className="mediazioni-elenco-unione">
          {gruppi.map((g) => (
            <li key={g.mediatori.map((m) => m.id).join()} className="riga">
              <span>
                <strong>{g.mediatori.map(nomeDi).join(' · ')}</strong>
                <span className="piccolo tenue"> — {g.motivi.map((m) => MOTIVI[m]).join(', ')}</span>
              </span>
              <span className="spinta">
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => rivedi(g)}>Rivedi</Bottone>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modale>
  )
}

/**
 * Scelta e conferma di un'unione: i mediatori coinvolti, quale tenere, quali unire; poi i numeri e
 * la conferma con il totale visto.
 */
export function UnisciMediatori({ iniziali, tenere: tenereIniziale, chiudi, fatto }: {
  iniziali: VoceUnione[]; tenere: string; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [voci, setVoci] = useState<VoceUnione[]>(iniziali)
  const [tenere, setTenere] = useState(tenereIniziale)
  const [unire, setUnire] = useState<Set<string>>(() => new Set(iniziali.map((v) => v.id).filter((id) => id !== tenereIniziale)))
  const [cerca, setCerca] = useState('')
  const [trovati, setTrovati] = useState<Mediatore[]>([])
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  // Altri mediatori da aggiungere all'unione, cercati per nome fra tutti, anche i non piu' attivi.
  useEffect(() => {
    const testo = cerca.trim()
    if (testo.length < 2) { setTrovati([]); return }
    let vivo = true
    const t = setTimeout(() => {
      api.get<Mediatore[]>(`/mediatori?${new URLSearchParams({ cerca: testo, attivo: 'tutti', limite: '20' })}`)
        .then((r) => { if (vivo) setTrovati(r) }).catch(() => { if (vivo) setTrovati([]) })
    }, 250)
    return () => { vivo = false; clearTimeout(t) }
  }, [cerca])

  const doppioni = voci.filter((v) => v.id !== tenere && unire.has(v.id)).map((v) => v.id)
  const cambiaUnire = (id: string, si: boolean) => setUnire((u) => {
    const n = new Set(u)
    if (si) n.add(id); else n.delete(id)
    return n
  })
  const aggiungi = (m: Mediatore) => {
    if (voci.some((v) => v.id === m.id)) return
    setVoci((v) => [...v, m]); cambiaUnire(m.id, true); setCerca(''); setAnteprima(null)
  }

  async function controlla() {
    setErrore(null); setInCorso(true)
    try {
      setAnteprima(await api.post<Anteprima>(`/mediatori/${tenere}/unisci/anteprima`, { doppioni }))
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  async function unisci() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    try {
      const r = await api.post<Anteprima>(`/mediatori/${tenere}/unisci`, { doppioni, attese: anteprima.righe.totale })
      fatto(`Fatto: ${quanti(r.doppioni.length, 'doppione unito', 'doppioni uniti')} a ${nomeDi(r.tenere)}`
        + (r.righe.totale ? `, che ora porta anche ${quanti(r.righe.totale, 'riga', 'righe')} di storico.` : '.'))
    } catch (e) {
      setErrore(messaggioErrore(e))
      if (e instanceof ErroreApi && e.codice === 'ANTEPRIMA_CAMBIATA') void controlla()
    } finally {
      setInCorso(false)
    }
  }

  if (anteprima) {
    const r = anteprima.righe
    const spostate = [
      r.mediazioni && quanti(r.mediazioni, 'mediazione', 'mediazioni'),
      r.interventi && quanti(r.interventi, 'intervento', 'interventi'),
      r.servizi && quanti(r.servizi, 'servizio del registro', 'servizi del registro'),
      r.erogazioni && quanti(r.erogazioni, 'servizio a un ente del vecchio archivio', 'servizi a enti del vecchio archivio'),
    ].filter(Boolean)
    return (
      <Modale larga titolo={`Unire a ${nomeDi(anteprima.tenere)}?`} chiudi={chiudi}
              fuocoIniziale="azioni"
              azioni={<>
                <Bottone variante="discreto" onClick={() => setAnteprima(null)}>Torna alla scelta</Bottone>
                <Bottone variante="critico" inCorso={inCorso} onClick={() => void unisci()}>
                  Unisci {quanti(anteprima.doppioni.length, 'doppione', 'doppioni')}
                </Bottone>
              </>}>
        <p>
          Si uniscono e poi si tolgono dall’albo: <strong>{anteprima.doppioni.map(nomeDi).join(', ')}</strong>.
        </p>
        <ul className="elenco-avviso">
          <li>
            {spostate.length
              ? <>Passano a {nomeDi(anteprima.tenere)}: {spostate.join(', ')}.</>
              : 'Nessuna mediazione, intervento o servizio da spostare.'}
          </li>
          {anteprima.mediazioni_in_programma > 0 && (
            <li>{quanti(anteprima.mediazioni_in_programma, 'mediazione è ancora in programma', 'mediazioni sono ancora in programma')}: restano tali, con il mediatore tenuto.</li>
          )}
          <li>Lingue dopo l’unione: {anteprima.lingue.length ? anteprima.lingue.join(', ') : 'nessuna indicata'}.</li>
          <li>
            {anteprima.utenza === 'passa' ? 'Il collegamento all’utenza del gestionale passa al mediatore tenuto.'
              : anteprima.utenza === 'resta' ? 'Il collegamento all’utenza del gestionale resta com’è.'
              : 'Nessuno dei due è collegato a un’utenza del gestionale.'}
          </li>
          <li>{anteprima.attivo_dopo ? 'Il mediatore tenuto è attivo.' : 'Il mediatore tenuto resta fra i «Non più attivi».'}</li>
        </ul>
        <p className="piccolo tenue">
          I nomi dei doppioni restano registrati: compaiono sotto il mediatore tenuto, la ricerca li trova, e una nuova
          importazione dal vecchio archivio non li ricrea. L’unione non si annulla dalla pagina.
        </p>
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
      </Modale>
    )
  }

  return (
    <Modale larga titolo="Unisci i doppioni" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!doppioni.length} onClick={() => void controlla()}>
                Controlla l’unione
              </Bottone>
            </>}>
      <p className="piccolo">
        Scegli il mediatore da <strong>tenere</strong> e spunta i suoi doppioni: lo storico dei doppioni passa a lui.
      </p>
      <fieldset className="gruppo-caselle">
        <legend>Mediatori</legend>
        {voci.map((v) => (
          <div key={v.id} className="mediazioni-riga-unione">
            <label className="riga">
              <input type="radio" name="tenere" checked={tenere === v.id}
                     onChange={() => { setTenere(v.id); setAnteprima(null) }} />
              <span>Tieni</span>
            </label>
            <label className="riga">
              <input type="checkbox" disabled={tenere === v.id} checked={tenere !== v.id && unire.has(v.id)}
                     onChange={(e) => cambiaUnire(v.id, e.target.checked)} />
              <span>
                <strong>{nomeDi(v)}</strong>{' '}
                {!v.attivo && <Etichetta>non più attivo</Etichetta>}{' '}
                {v.utente_id && <Etichetta tono="informativo">usa il gestionale</Etichetta>}
                {v.dal_vecchio_archivio && <span className="piccolo tenue"> · dal vecchio archivio</span>}
                {v.usi !== undefined && <span className="piccolo tenue"> · {quanti(v.usi, 'riga', 'righe')} di storico</span>}
              </span>
            </label>
          </div>
        ))}
      </fieldset>
      <Campo tipo="search" etichetta="Aggiungi un altro mediatore" valore={cerca} cambia={setCerca} maxLength={120}
             aiuto="Scrivi almeno due lettere del nome o del cognome." />
      {trovati.filter((m) => !voci.some((v) => v.id === m.id)).length > 0 && (
        <ul className="mediazioni-elenco-unione">
          {trovati.filter((m) => !voci.some((v) => v.id === m.id)).map((m) => (
            <li key={m.id} className="riga">
              <span>{nomeDi(m)}{!m.attivo && <span className="piccolo tenue"> · non più attivo</span>}</span>
              <span className="spinta">
                <Bottone variante="discreto" dimensione="piccolo" onClick={() => aggiungi(m)}>Aggiungi</Bottone>
              </span>
            </li>
          ))}
        </ul>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
